import {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery as useBackendQuery} from 'react-query';

import Skeleton from 'react-loading-skeleton';
import {getUserProductReviews} from '../backendApi';
import UserReviewCarouselPaginated from './carousels/UserReviewCarouselPaginated.client';

interface ProductUserReviewBlockProps {
  openEditReviewModal: () => void;
  productUuid: string;
}

const SwiperBreakpointsConfig = {
  640: {
    slidesPerView: 1,
    spaceBetween: 0,
  },
  768: {
    slidesPerView: 3,
    spaceBetween: 25,
  },
  1440: {
    slidesPerView: 4,
    spaceBetween: 36,
  },
};

export default function ProductUserReviewBlock({
  openEditReviewModal,
  productUuid,
}: ProductUserReviewBlockProps): ReactElement {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    SwiperBreakpointsConfig[1440].slidesPerView,
  );
  const setSwiperPageSize = useCallback(() => {
    if (window.innerWidth < 768) {
      setPageSize(SwiperBreakpointsConfig[640].slidesPerView);
    } else if (window.innerWidth < 1440) {
      setPageSize(SwiperBreakpointsConfig[768].slidesPerView);
    } else {
      setPageSize(SwiperBreakpointsConfig[1440].slidesPerView);
    }
  }, []);

  useEffect(() => {
    setSwiperPageSize();
    window.addEventListener('resize', setSwiperPageSize);
    return () => window.removeEventListener('resize', setSwiperPageSize);
  }, [setSwiperPageSize]);

  const {data, isLoading} = useBackendQuery(
    ['productDetailUserReviews', productUuid, pageSize, currentPage],
    () => getUserProductReviews({productUuid, pageSize, page: currentPage}),
    {
      enabled: !!productUuid,
      retry: false,
    },
  );

  const totalReviews = useMemo(() => data?.count || 0, [data?.count]);
  const productUserReviews = useMemo(
    () => data?.results ?? [],
    [data?.results],
  );
  const prevUrl = useMemo(() => data?.previous, [data?.previous]);
  const nextUrl = useMemo(() => data?.next, [data?.next]);

  return (
    <section id="product-reviews">
      <div className="reviews product-page-reviews container">
        <h3 className="title-1">
          User Reviews {totalReviews > 0 && `(${totalReviews})`}
        </h3>
        <div className="reviews__top-buttons">
          <button
            onClick={openEditReviewModal}
            className="button button_has_icon button-plus js-modal-opener"
          >
            Add a review <i className="icon-plus-circle"></i>
          </button>
        </div>
        {isLoading && (
          <div className="reviews__list carousel-outer">
            <div className="reviews__carousel">
              <Skeleton count={1} height={60} />
              <Skeleton count={1} height={250} />
              <Skeleton count={1} height={50} />
            </div>
          </div>
        )}
        {productUserReviews && productUserReviews.length > 0 ? (
          <UserReviewCarouselPaginated
            reviews={productUserReviews}
            prevUrl={prevUrl}
            nextUrl={nextUrl}
            setCurrentPage={setCurrentPage}
            showImage={false}
            swiperBreakpointsConfig={SwiperBreakpointsConfig}
          />
        ) : (
          !isLoading && <div className="reviews__list">No reviews yet</div>
        )}
      </div>
    </section>
  );
}
